import { useRef } from "react"
import { makeStyles } from "tss-react/mui"
import { alpha } from "@mui/material/styles"
import { Player, PlayerEvent } from "@lottiefiles/react-lottie-player"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarPlus } from "@fortawesome/pro-regular-svg-icons"
import { Box, DialogContent, Grid, Stack, Typography } from "@mui/material"
import { faCalendarCheck, faMugSaucer, faRightFromBracket } from "@fortawesome/pro-solid-svg-icons"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { StandardDialog } from "../../../dialogs/StandardDialog"
import { StandardButton } from "../../../buttons/StandardButton"
import loadingAnimationSuccess from "../assets/success.json"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "2.5rem",
        paddingTop: "0",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        alignItems: "center",
        color: theme.palette.tailwind.slate[950],
    },
    animation: {
        height: "4.5rem",
    },
    headerStack: {
        width: "100%",
        alignItems: "center",
    },
    description: {
        alignSelf: "stretch",
        textAlign: "left",
        marginTop: ".5rem",
    },
    box: {
        width: "100%",
        padding: ".75rem",
        borderRadius: ".75rem",
        cursor: "pointer",
        boxShadow: `0px 2px 6px 0px ${alpha(theme.palette.common.black, 0.08)}`,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        transition: "all 0.3s ease-out",
    },
    boxPurple: {
        "&:hover": {
            borderColor: theme.palette.tailwind.purple[300],
        },
    },
    boxBlue: {
        "&:hover": {
            borderColor: theme.palette.tailwind.blue[300],
        },
    },
    boxRed: {
        "&:hover": {
            borderColor: theme.palette.tailwind.red[300],
        },
    },
    boxTeal: {
        "&:hover": {
            borderColor: theme.palette.tailwind.teal[300],
        },
    },
    iconWrap: {
        width: "3.25rem",
        height: "3.25rem",
        borderRadius: ".5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    iconWrapPurple: {
        background: theme.palette.tailwind.purple[200],
        color: theme.palette.tailwind.purple[900],
    },
    iconWrapRed: {
        background: theme.palette.tailwind.red[200],
        color: theme.palette.tailwind.red[900],
    },
    iconWrapTeal: {
        background: theme.palette.tailwind.teal[200],
        color: theme.palette.tailwind.teal[900],
    },
    button: {
        textDecoration: "underline",
        fontSize: "1rem",
        "& .MuiButton-startIcon": {
            marginBottom: ".125rem",
        },
    },
}))

type FinalSuccessDialogContentProps = Readonly<{
    open: boolean
    onClose: () => void
    hideClose?: boolean
    icsAppointmentLink?: string
    handleFinish: () => void
    handleAddMeetingService?: () => void
    handleNewOrder?: () => void
    handleNewService?: () => void
    handleAddResourceBooking?: () => void
    isUpdate?: boolean
    isOutlookFlow?: boolean
    orderId?: number
}>

export function FinalSuccessDialogContent({
    open,
    onClose,
    hideClose = false,
    icsAppointmentLink,
    handleFinish,
    handleAddMeetingService,
    handleNewOrder,
    handleNewService,
    handleAddResourceBooking,
    isUpdate = false,
    isOutlookFlow = false,
    orderId,
}: FinalSuccessDialogContentProps) {
    const playerRef = useRef<Player>(null)
    const { classes, cx } = useStyles()

    return (
        <StandardDialog
            dialogTitle={
                <>
                    <Player
                        ref={playerRef}
                        src={loadingAnimationSuccess}
                        autoplay
                        speed={1}
                        keepLastFrame
                        className={classes.animation}
                        onEvent={(event) => {
                            if (event === PlayerEvent.Complete) playerRef.current?.pause()
                        }}
                    />
                </>
            }
            open={open}
            onClose={hideClose ? () => {} : onClose}
            maxWidthProp="sm"
            scroll="body"
            hideCloseIcon={hideClose}
        >
            <DialogContent className={classes.dialogContent}>
                <Stack gap="1rem" className={classes.headerStack}>
                    {handleAddMeetingService &&
                        (orderId ? (
                            <LocalizedStrict
                                id="create-order-success-dialog-resource-booking-title-with-order-id"
                                vars={{ orderId: orderId.toString() }}
                            >
                                <Typography variant="h5" fontWeight={500}>
                                    Booking #{orderId} created!
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="create-order-success-dialog-resource-booking-title">
                                <Typography variant="h5" fontWeight={500}>
                                    Booking created!
                                </Typography>
                            </LocalizedStrict>
                        ))}
                    {handleAddResourceBooking &&
                        (orderId ? (
                            <LocalizedStrict
                                id="create-order-success-dialog-meeting-service-title-with-order-id"
                                vars={{ orderId: orderId.toString() }}
                            >
                                <Typography variant="h5" fontWeight={500}>
                                    Order #{orderId} created!
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="create-order-success-dialog-meeting-service-title">
                                <Typography variant="h5" fontWeight={500}>
                                    Order completed!
                                </Typography>
                            </LocalizedStrict>
                        ))}
                </Stack>
                <Grid container gap=".75rem">
                    <LocalizedStrict id="create-order-success-dialog-description">
                        <Typography variant="body1" className={classes.description}>
                            What do you want to do next?
                        </Typography>
                    </LocalizedStrict>
                    {handleAddMeetingService && (
                        <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems="center"
                            className={cx(classes.box, classes.boxTeal)}
                            onClick={handleAddMeetingService}
                        >
                            <Box className={cx(classes.iconWrap, classes.iconWrapTeal)}>
                                <FontAwesomeIcon size="lg" icon={faMugSaucer} />
                            </Box>
                            {isUpdate ? (
                                <LocalizedStrict id="create-order-success-dialog-action-meeting-service-update">
                                    <Typography variant="subtitle1">Update meeting service</Typography>
                                </LocalizedStrict>
                            ) : (
                                <LocalizedStrict id="create-order-success-dialog-action-meeting-service">
                                    <Typography variant="subtitle1">Add meeting service to order</Typography>
                                </LocalizedStrict>
                            )}
                        </Stack>
                    )}
                    {handleAddResourceBooking && (
                        <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems="center"
                            className={cx(classes.box, classes.boxPurple)}
                            onClick={handleAddResourceBooking}
                        >
                            <Box className={cx(classes.iconWrap, classes.iconWrapPurple)}>
                                <FontAwesomeIcon size="lg" icon={faCalendarCheck} />
                            </Box>
                            {isUpdate ? (
                                <LocalizedStrict id="create-order-success-dialog-action-update-resource-booking">
                                    <Typography variant="subtitle1">Update resource booking</Typography>
                                </LocalizedStrict>
                            ) : (
                                <LocalizedStrict id="create-order-success-dialog-action-add-resource-booking">
                                    <Typography variant="subtitle1">Add resource booking</Typography>
                                </LocalizedStrict>
                            )}
                        </Stack>
                    )}

                    {handleNewOrder && (
                        <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems="center"
                            className={cx(classes.box, classes.boxPurple)}
                            onClick={handleNewOrder}
                        >
                            <Box className={cx(classes.iconWrap, classes.iconWrapPurple)}>
                                <FontAwesomeIcon size="lg" icon={faCalendarCheck} />
                            </Box>
                            <LocalizedStrict id="create-order-success-dialog-action-create-booking">
                                <Typography variant="subtitle1">Create another booking</Typography>
                            </LocalizedStrict>
                        </Stack>
                    )}
                    {handleNewService && (
                        <Stack
                            flexDirection="row"
                            gap="1rem"
                            alignItems="center"
                            className={cx(classes.box, classes.boxTeal)}
                            onClick={handleNewService}
                        >
                            <Box className={cx(classes.iconWrap, classes.iconWrapTeal)}>
                                <FontAwesomeIcon size="lg" icon={faMugSaucer} />
                            </Box>
                            <LocalizedStrict id="create-order-success-dialog-action-create-meeting-service">
                                <Typography variant="subtitle1">Create another meeting service</Typography>
                            </LocalizedStrict>
                        </Stack>
                    )}
                    <Stack
                        flexDirection="row"
                        gap="1rem"
                        alignItems="center"
                        className={cx(classes.box, classes.boxRed)}
                        onClick={handleFinish}
                    >
                        <Box className={cx(classes.iconWrap, classes.iconWrapRed)}>
                            <FontAwesomeIcon size="lg" icon={faRightFromBracket} />
                        </Box>
                        <LocalizedStrict id="create-order-success-dialog-action-finish">
                            <Typography variant="subtitle1">I'm finished</Typography>
                        </LocalizedStrict>
                    </Stack>
                </Grid>
                {icsAppointmentLink && !isOutlookFlow && (
                    <LocalizedStrict id="create-order-success-dialog-action-calendar">
                        <StandardButton
                            variant="text"
                            size="small"
                            fullWidth={false}
                            onClick={() => window.open(icsAppointmentLink, "_blank")}
                            startIcon={<FontAwesomeIcon icon={faCalendarPlus} size="xs" />}
                            className={classes.button}
                        >
                            Add meeting to calendar
                        </StandardButton>
                    </LocalizedStrict>
                )}
            </DialogContent>
        </StandardDialog>
    )
}
